'use client';

import { memo, useCallback, useMemo } from 'react';

import { type Board } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { useLibraryPermissions } from '~/components/LibraryBeta/hooks/useLibraryPermissions';
import { LibraryHeaderCreateButton } from '~/components/LibraryBeta/LibraryHeaderCreateButton';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { CreateDropdownButton } from '~/components/Shared/HeaderBar/CreateDropdownButton';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { getCreateMenuOptions } from '~/utils/dropdownMenuOptions/getCreateMenuOptions';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export type LibraryTopBarAddButtonProps = {
  library: NonNullable<Board['library']>;
};

const LibraryTopBarLegacyAddButton = memo(({ library }: LibraryTopBarAddButtonProps) => {
  const { libraryPermissions } = useLibraryPermissions({ libraryId: library.id });
  const [setShowCreatePrivateBoardModal] = useAirModal(CreateBoardModal);
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const isRightPanelOpen = useWorkspaceRightPanelOpen();
  const { openDraftCanvas } = useDraftCanvas();

  const handleCreateBoard = useCallback(() => {
    setShowCreatePrivateBoardModal({
      initialLibrary: library,
      trackLocation: 'library',
    });
  }, [library, setShowCreatePrivateBoardModal]);

  const handleCreateCanvas = useCallback(() => {
    openDraftCanvas({
      analyticsContext: { source: 'library', library_id: library.id },
    });
  }, [library.id, openDraftCanvas]);

  const options = useMemo(
    () =>
      getCreateMenuOptions({
        onCreateBoard: canCreateBoard(libraryPermissions) ? handleCreateBoard : undefined,
        onCreateCanvas: generativeCanvasEnabled ? handleCreateCanvas : undefined,
      }),
    [generativeCanvasEnabled, handleCreateBoard, handleCreateCanvas, libraryPermissions],
  );

  if (!isRightPanelOpen) {
    return <CreateDropdownButton options={options} />;
  }

  return null;
});

LibraryTopBarLegacyAddButton.displayName = 'LibraryTopBarLegacyAddButton';

export const LibraryTopBarAddButton = memo(({ library }: LibraryTopBarAddButtonProps) => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const { libraryPermissions } = useLibraryPermissions({ libraryId: library.id });
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  if (!ui2026) {
    return <LibraryTopBarLegacyAddButton library={library} />;
  }

  return (
    <LibraryHeaderCreateButton
      data-testid="LIBRARY_HEADER_CREATE_BUTTON"
      library={library}
      libraryPermissions={libraryPermissions}
      workspacePermissions={workspacePermissions}
    />
  );
});

LibraryTopBarAddButton.displayName = 'LibraryTopBarAddButton';
